
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { User, Company, Lead, UserRole, Task } from '../types';
import { api, exportToCSV } from '../services/apiService';
import LeadDetailsModal from '../components/LeadDetailsModal';
import SelectField from '../components/SelectField';
import AddLeadModal from '../components/AddLeadModal';
import CalendarView from '../components/CalendarView';
import OpportunitiesView from './OpportunitiesView';
import TasksView from './TasksView';
import TaskWidget from '../components/TaskWidget';
import { DEFAULT_REVENUE_COLORS } from '../constants';

interface UserDashboardProps {
  user: User;
  company: Company;
  activeView: string;
}

const UserDashboard: React.FC<UserDashboardProps> = ({ user, company, activeView }) => {
  // Couleurs effectives : config entreprise ou couleurs par défaut
  const effectiveRevenueColors = company.revenueColors && Object.keys(company.revenueColors).length > 0
    ? company.revenueColors
    : DEFAULT_REVENUE_COLORS;

  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [showAddLead, setShowAddLead] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);

  // Filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTag, setSearchTag] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterSector, setFilterSector] = useState<string>('all');

  // Tri
  const [sortConfig, setSortConfig] = useState<{ key: keyof Lead | 'revenue'; direction: 'asc' | 'desc' } | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportDateFrom, setExportDateFrom] = useState('');
  const [exportDateTo, setExportDateTo] = useState('');
  const exportBtnRef = useRef<HTMLButtonElement>(null);
  const exportPanelRef = useRef<HTMLDivElement>(null);
  const [exportMenuRect, setExportMenuRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [userLeads, companyUsers, taskData] = await Promise.all([
            api.getLeads({ companyId: user.companyId, userId: user.id, role: user.role }),
            api.getCompanyUsers(user.companyId),
            api.getTasks({ companyId: user.companyId })
        ]);
        setLeads(userLeads || []);
        setUsers(companyUsers || []);
        setTasks(taskData || []);
        await api.checkUpcomingTaskReminders(user.id);
      } catch (e) {
        console.error("User dash fetch error", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inBtn = exportBtnRef.current?.contains(target);
      const inPanel = exportPanelRef.current?.contains(target);
      if (!inBtn && !inPanel) setShowExportMenu(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [user.id, user.companyId, activeView]); // Utilisation de user.id au lieu de user

  const handleAddLead = async (leadData: Omit<Lead, 'id' | 'createdAt'>) => {
    const tempId = 'lead_' + Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
    const createdAt = new Date().toISOString();

    const optimisticLead: Lead = {
      id: tempId,
      companyId: company.id,
      clientCompanyName: leadData.clientCompanyName || '',
      tag: leadData.tag || '',
      sectorActivity: leadData.sectorActivity || '',
      name: leadData.name || 'Nouveau Prospect',
      email: leadData.email || '',
      phone: leadData.phone || '',
      status: leadData.status || 'new',
      assignedTo: leadData.assignedTo || user.id,
      createdAt: createdAt,
      potentialRevenue: leadData.potentialRevenue || '',
      additionalInfo: leadData.additionalInfo || '',
      isFavorite: leadData.isFavorite || false
    };

    setLeads(prev => [optimisticLead, ...prev]);
    setSearchTerm('');

    try {
        const confirmedLead = await api.createLead({
            ...optimisticLead,
            id: tempId
        });
        if (confirmedLead) {
             setLeads(prev => prev.map(l => l.id === tempId ? confirmedLead : l));
        }
    } catch (e) {
        console.error("Failed to create lead", e);
        setLeads(prev => prev.filter(l => l.id !== tempId));
        alert("La creation en base a echoue. Le prospect n'a pas ete enregistre.");
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const dataToExport = await api.getLeads({
        companyId: user.companyId, 
        userId: user.id, 
        role: user.role,
        dateFrom: exportDateFrom, 
        dateTo: exportDateTo
      });
      const formatted = dataToExport.map(l => ({
        ID: l.id, Nom: l.name, Email: l.email, Statut: l.status, Date: l.createdAt
      }));
      exportToCSV(formatted, `mes_leads_${user.name}.csv`);
      setShowExportMenu(false);
    } finally {
      setIsExporting(false);
    }
  };

  const handleLeadUpdate = (updatedLead: Lead) => {
    setLeads(prevLeads => prevLeads.map(l => String(l.id) === String(updatedLead.id) ? updatedLead : l));
    if (selectedLead && String(selectedLead.id) === String(updatedLead.id)) {
      setSelectedLead(updatedLead);
    }
  };

  const handleDeleteLead = async (leadId: string) => {
    try {
      await api.deleteLead(leadId);
      setLeads(prev => prev.filter(l => String(l.id) !== String(leadId)));
      setSelectedLead(null);
    } catch (err) {
      console.error('Erreur suppression prospect:', err);
    }
  };

  const handleSort = (key: keyof Lead | 'revenue') => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const formatDateShort = (dateStr: string) => {
      const d = new Date(dateStr);
      return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  };

  const sectorOptions = useMemo(() => {
    return Array.from(
      new Set(
        leads
          .map(l => String(l.sectorActivity || '').trim())
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
  }, [leads]);

  // Filtrage (Déplacé avant le return)
  const filteredLeads = useMemo(() => {
    return leads.filter(l => {
        const matchesStatus = filterStatus === 'all' || l.status === filterStatus;
        const matchesSector = filterSector === 'all' || String(l.sectorActivity || '') === String(filterSector);
        const matchesSearch = (l.name || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
                              (l.email || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesTag = !searchTag.trim() || (l.tag || '').toLowerCase().includes(searchTag.toLowerCase());
        return matchesStatus && matchesSector && matchesSearch && matchesTag;
    });
  }, [leads, filterStatus, filterSector, searchTerm, searchTag]);

  // Tri (Déplacé avant le return)
  const sortedLeads = useMemo(() => {
    if (!sortConfig) return filteredLeads;
    
    return [...filteredLeads].sort((a, b) => {
        let valA: any = a[sortConfig.key as keyof Lead] || '';
        let valB: any = b[sortConfig.key as keyof Lead] || '';

        if (sortConfig.key === 'revenue' || sortConfig.key === 'potentialRevenue') {
            valA = parseInt((a.potentialRevenue || '0').replace(/\D/g, '')) || 0;
            valB = parseInt((b.potentialRevenue || '0').replace(/\D/g, '')) || 0;
        } 
        else if (sortConfig.key === 'createdAt') {
            valA = new Date(valA).getTime();
            valB = new Date(valB).getTime();
        }
        else {
             valA = String(valA).toLowerCase();
             valB = String(valB).toLowerCase();
        }

        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });
  }, [filteredLeads, sortConfig]);

  const totalPages = Math.ceil(sortedLeads.length / itemsPerPage);
  const paginatedLeads = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedLeads.slice(start, start + itemsPerPage);
  }, [sortedLeads, currentPage, itemsPerPage]);

  useEffect(() => { setCurrentPage(1); }, [filterStatus, filterSector, searchTerm, searchTag]);

  // Return conditionnel APRÈS tous les hooks
  if (activeView === 'planning') return <CalendarView currentUser={user} leads={leads} companyUsers={users} />;
  if (activeView === 'tasks') return <TasksView user={user} company={company} />;
  if (activeView === 'opportunities') return <OpportunitiesView user={user} company={company} />;

  const statsBreakdown = {
    new: leads.filter(l => l.status === 'new').length,
    qualified: leads.filter(l => l.status === 'qualified').length,
    won: leads.filter(l => l.status === 'won').length,
    lost: leads.filter(l => l.status === 'lost').length,
  };

  const columns = [
    { id: 'fav', label: 'Favoris ★', color: 'bg-amber-50 text-amber-700 border-amber-100', dot: 'bg-amber-500' },
    { id: 'new', label: 'Nouveaux', color: 'bg-indigo-50 text-indigo-700 border-indigo-100', dot: 'bg-indigo-500' },
    { id: 'contacted', label: '1er Contact', color: 'bg-sky-50 text-sky-700 border-sky-100', dot: 'bg-sky-500' },
    { id: 'qualified', label: 'Qualifiés', color: 'bg-purple-50 text-purple-700 border-purple-100', dot: 'bg-purple-500' },
    { id: 'won', label: 'Gagnés', color: 'bg-emerald-50 text-emerald-700 border-emerald-100', dot: 'bg-emerald-500' },
    { id: 'lost', label: 'Perdus', color: 'bg-slate-50 text-slate-700 border-slate-200', dot: 'bg-slate-500' },
  ];

  const getLeadsForColumn = (colId: string) => {
      if (colId === 'fav') return filteredLeads.filter(l => l.isFavorite);
      return filteredLeads.filter(l => !l.isFavorite && l.status === colId);
  };

  const SortIcon = ({ colKey }: { colKey: keyof Lead | 'revenue' }) => {
    if (sortConfig?.key !== colKey) return <svg className="w-3 h-3 opacity-30 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" /></svg>;
    return sortConfig.direction === 'asc' 
        ? <svg className="w-3 h-3 text-indigo-600 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" /></svg>
        : <svg className="w-3 h-3 text-indigo-600 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>;
  };

  return (
    <div className="orion-dashboard space-y-5 animate-in fade-in duration-500 flex-1 min-h-0 flex flex-col">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 flex-shrink-0">
        <div className="orion-kpi orion-kpi-glossy p-5 rounded-xl border border-slate-100 shadow-sm">
          <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Prospect</div>
          <div className="text-2xl font-black text-indigo-600">{leads.length}</div>
        </div>
        <div className="orion-kpi orion-kpi-glossy p-5 rounded-xl border border-slate-100 shadow-sm">
          <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Gagnés</div>
          <div className="text-2xl font-black text-emerald-500">{statsBreakdown.won}</div>
        </div>
        <div className="orion-kpi orion-kpi-glossy p-5 rounded-xl border border-slate-100 shadow-sm">
          <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">En Cours</div>
          <div className="text-2xl font-black text-amber-500">{statsBreakdown.new + statsBreakdown.qualified}</div>
        </div>
        <div className="orion-kpi orion-kpi-glossy p-5 rounded-xl border border-slate-100 shadow-sm">
          <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Taux Transfo</div>
          <div className="text-2xl font-black text-slate-800">
             {statsBreakdown.won + statsBreakdown.lost > 0 ? Math.round((statsBreakdown.won / (statsBreakdown.won + statsBreakdown.lost)) * 100) : 0}%
          </div>
        </div>
      </div>

      {activeView === 'dashboard' && (
        <TaskWidget tasks={tasks} title="Tâches à faire" />
      )}

      {/* Barre d'action */}
      <div className="orion-toolbar flex flex-col md:flex-row justify-between items-center gap-3 p-1.5 rounded-xl border border-slate-200 shadow-sm flex-shrink-0">
          <div className="w-full md:w-auto px-2 flex items-center">
            <input 
              type="text" 
              placeholder="Recherche nom, email..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full md:w-80 bg-slate-50 border-none rounded-xl pl-4 pr-4 py-2.5 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500 text-slate-700"
            />
            <input
              type="text"
              placeholder="Recherche tag..."
              value={searchTag}
              onChange={(e) => setSearchTag(e.target.value)}
              className="w-full md:w-52 bg-slate-50 border-none rounded-xl ml-2 pl-4 pr-4 py-2.5 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500 text-slate-700"
            />
            {/* Filtre Status */}
            <SelectField
              value={filterStatus}
              onChange={setFilterStatus}
              options={[
                { value: 'all', label: 'Tous les statuts' },
                { value: 'new', label: 'Nouveaux' },
                { value: 'contacted', label: 'Contactés' },
                { value: 'qualified', label: 'Qualifiés' },
                { value: 'won', label: 'Gagnés' },
                { value: 'lost', label: 'Perdus' },
              ]}
              className="orion-dashboard-select ml-2 w-40 rounded-xl px-3 py-2.5 text-xs font-bold"
            />
            <SelectField
              value={filterSector}
              onChange={setFilterSector}
              options={[
                { value: 'all', label: 'Tous les secteurs' },
                ...sectorOptions.map(s => ({ value: s, label: s }))
              ]}
              className="orion-dashboard-select ml-2 w-40 rounded-xl px-3 py-2.5 text-xs font-bold"
            />
          </div>
          
          <div className="flex items-center gap-2 px-2">
              <button 
                onClick={() => setShowAddLead(true)}
                className="orion-primary-btn text-white px-5 py-2.5 rounded-xl text-xs font-black flex items-center gap-2 transition-all shadow-lg"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
                NOUVEAU
              </button>

              <div className="relative">
                <button
                  ref={exportBtnRef}
                  type="button"
                  onClick={() => {
                    if (exportBtnRef.current) setExportMenuRect(exportBtnRef.current.getBoundingClientRect());
                    setShowExportMenu(!showExportMenu);
                  }}
                  className="orion-icon-btn text-slate-600 px-3 py-2.5 rounded-xl transition-all"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                </button>
                {showExportMenu && exportMenuRect && createPortal(
                  <div
                    ref={exportPanelRef}
                    className="orion-popover w-64 rounded-2xl shadow-xl p-4 animate-in slide-in-from-top-2"
                    style={{
                      position: 'fixed',
                      top: `${exportMenuRect.bottom + 8}px`,
                      right: `${window.innerWidth - exportMenuRect.right}px`,
                      zIndex: 9999,
                    }}
                  >
                    <div className="space-y-3 mb-4">
                        <input type="date" value={exportDateFrom} onChange={e => setExportDateFrom(e.target.value)} className="w-full orion-lead-select rounded-lg px-2 py-2 text-[10px] font-bold" />
                        <input type="date" value={exportDateTo} onChange={e => setExportDateTo(e.target.value)} className="w-full orion-lead-select rounded-lg px-2 py-2 text-[10px] font-bold" />
                    </div>
                    <button onClick={handleExport} disabled={isExporting} className="w-full orion-primary-btn text-white py-3 rounded-xl text-[10px] font-black uppercase tracking-widest">
                        {isExporting ? '...' : 'Télécharger CSV'}
                    </button>
                  </div>,
                  document.body
                )}
              </div>
            </div>
      </div>

      {activeView === 'dashboard' ? (
        // VUE TUNNEL (DASHBOARD)
        <div className="flex-1 overflow-y-auto pb-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4 px-1">
            {columns.map(col => {
                const colLeads = getLeadsForColumn(col.id);
                const totalRevenue = colLeads.reduce((acc, l) => {
                    const match = (l.potentialRevenue || '').match(/(\d[\d\s]*)/);
                    return acc + (match ? parseInt(match[0].replace(/\s/g, '')) : 0);
                }, 0);

                return (
                <div key={col.id} className="orion-kanban-column flex flex-col min-h-[360px] max-h-[520px] rounded-2xl border border-slate-100 overflow-hidden group">
                    {/* Header Colonne */}
                    <div className={`orion-kanban-header p-4 border-b ${col.color.replace('text', 'border').split(' ')[2]} backdrop-blur-sm sticky top-0 z-10`}>
                        <div className="flex justify-between items-center mb-1">
                            <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${col.dot}`}></div>
                                <h3 className="text-xs font-black text-slate-800 uppercase tracking-tight">{col.label}</h3>
                            </div>
                            <span className="bg-slate-100 text-slate-500 text-[10px] font-bold px-2 py-0.5 rounded-md">{colLeads.length}</span>
                        </div>
                        {totalRevenue > 0 && (
                            <div className="text-[10px] font-bold text-slate-400 text-right">
                            ~{totalRevenue.toLocaleString()} {company.currency || '€'}
                            </div>
                        )}
                    </div>

                    {/* Liste Cartes */}
                    <div className="flex-1 overflow-y-auto p-3 space-y-3 custom-scrollbar">
                        {colLeads.map(lead => {
                            const revColor = lead.potentialRevenue ? effectiveRevenueColors[lead.potentialRevenue] : undefined;
                            return (
                            <div
                            key={lead.id}
                            onClick={() => setSelectedLead(lead)}
                            className="orion-lead-card p-4 rounded-xl border border-slate-100 shadow-sm hover:scale-[1.02] transition-all cursor-pointer group/card"
                            style={revColor ? { '--rev-border-color': revColor } as React.CSSProperties : undefined}
                            >
                            <div className="flex justify-between items-start mb-3">
                                <div className="flex items-center gap-3">
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black text-white shadow-sm ${col.dot}`}>
                                            {lead.name.charAt(0)}
                                        </div>
                                        <div className="leading-tight">
                                            <div className="text-sm font-bold text-slate-800 group-hover/card:text-indigo-600 transition-colors line-clamp-1">{lead.name}</div>
                                            <div className="text-[10px] text-slate-400 font-medium truncate w-32">{lead.email}</div>
                                        </div>
                                </div>
                                {lead.isFavorite && col.id !== 'fav' && <span className="text-amber-400 text-xs">★</span>}
                            </div>

                            <div className="flex items-center justify-between pt-3 border-t border-slate-50">
                                <span className="text-[10px] font-black uppercase text-indigo-400 tracking-wider">
                                    {lead.status}
                                </span>
                                <div className="text-right">
                                    <div className="flex items-center justify-end gap-1">
                                        {revColor && <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: revColor }} />}
                                        <span className="text-[10px] font-black" style={revColor ? { color: revColor } : { color: '#475569' }}>
                                            {lead.potentialRevenue || '-'}
                                        </span>
                                    </div>
                                    <div className="text-[8px] font-bold text-slate-300">{formatDateShort(lead.createdAt)}</div>
                                </div>
                            </div>
                            </div>
                            );
                        })}
                    </div>
                </div>
                );
            })}
            </div>
        </div>
      ) : (
        // VUE LISTE (PROSPECTS) - AVEC TRI ET FILTRE
        <div className="orion-table-shell flex-1 rounded-[2.5rem] border border-slate-200 shadow-sm overflow-hidden flex flex-col">
            <div className="overflow-x-auto custom-scrollbar flex-1">
                <table className="w-full text-left border-collapse">
                    <thead className="orion-table-head sticky top-0 z-10">
                        <tr className="text-slate-400 text-[10px] font-black uppercase tracking-widest border-b border-slate-100">
                            <th className="orion-table-th px-6 py-5 cursor-pointer transition-colors" onClick={() => handleSort('name')}>
                                <div className="flex items-center">Prospect <SortIcon colKey="name" /></div>
                            </th>
                            <th className="orion-table-th px-6 py-5 cursor-pointer transition-colors" onClick={() => handleSort('status')}>
                                <div className="flex items-center">Statut <SortIcon colKey="status" /></div>
                            </th>
                            <th className="orion-table-th px-6 py-5 cursor-pointer transition-colors" onClick={() => handleSort('revenue')}>
                                <div className="flex items-center">Montant <SortIcon colKey="revenue" /></div>
                            </th>
                            <th className="orion-table-th px-6 py-5 cursor-pointer transition-colors" onClick={() => handleSort('createdAt')}>
                                <div className="flex items-center">Date <SortIcon colKey="createdAt" /></div>
                            </th>
                            <th className="px-6 py-5 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {paginatedLeads.map((lead) => {
                        const revColor = lead.potentialRevenue ? effectiveRevenueColors[lead.potentialRevenue] : undefined;
                        return (
                        <tr key={lead.id} onClick={() => setSelectedLead(lead)} className="orion-table-row-hover cursor-pointer group transition-colors"
                          style={revColor ? { borderLeft: `3px solid ${revColor}` } : undefined}
                        >
                            <td className="px-6 py-4">
                                <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-black text-white shadow-sm ${
                                        lead.status === 'won' ? 'bg-emerald-500' :
                                        lead.status === 'new' ? 'bg-indigo-500' : 'bg-slate-400'
                                    }`}>
                                        {lead.name.charAt(0)}
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <div className="font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">{lead.name}</div>
                                            {lead.isFavorite && <span className="text-amber-400 text-xs">★</span>}
                                        </div>
                                        <div className="text-xs text-slate-500">{lead.email}</div>
                                    </div>
                                </div>
                            </td>
                            <td className="px-6 py-4">
                                <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase border ${
                                    lead.status === 'won' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                                    lead.status === 'new' ? 'bg-indigo-50 text-indigo-700 border-indigo-100' :
                                    lead.status === 'lost' ? 'bg-rose-50 text-rose-700 border-rose-100' :
                                    lead.status === 'qualified' ? 'bg-purple-50 text-purple-700 border-purple-100' :
                                    'bg-sky-50 text-sky-700 border-sky-100'
                                }`}>
                                    {lead.status === 'new' ? 'Nouveau' :
                                     lead.status === 'contacted' ? 'Contacté' :
                                     lead.status === 'qualified' ? 'Qualifié' :
                                     lead.status === 'won' ? 'Gagné' : 'Perdu'}
                                </span>
                            </td>
                            <td className="px-6 py-4">
                                {lead.potentialRevenue ? (
                                  <span
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-black"
                                    style={revColor ? {
                                      backgroundColor: revColor + '18',
                                      color: revColor,
                                      border: `1px solid ${revColor}50`
                                    } : { color: '#475569' }}
                                  >
                                    {revColor && <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: revColor }} />}
                                    {lead.potentialRevenue}
                                  </span>
                                ) : (
                                  <span className="text-slate-400 text-sm font-bold">-</span>
                                )}
                            </td>
                            <td className="px-6 py-4 text-xs font-bold text-slate-500">
                                {formatDateShort(lead.createdAt)}
                            </td>
                            <td className="px-6 py-4 text-right">
                                <button type="button" title="Voir le prospect" className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                                </button>
                            </td>
                        </tr>
                        );
                        })}
                    </tbody>
                </table>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-3 border-t border-slate-100">
                <span className="text-xs font-bold text-slate-400">
                  {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, sortedLeads.length)} sur {sortedLeads.length}
                </span>
                <div className="flex items-center gap-1">
                  <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="px-2 py-1 rounded-lg text-xs font-bold text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed">{'<<'}</button>
                  <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-2 py-1 rounded-lg text-xs font-bold text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed">{'<'}</button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let page: number;
                    if (totalPages <= 5) { page = i + 1; }
                    else if (currentPage <= 3) { page = i + 1; }
                    else if (currentPage >= totalPages - 2) { page = totalPages - 4 + i; }
                    else { page = currentPage - 2 + i; }
                    return (
                      <button key={page} onClick={() => setCurrentPage(page)} className={`w-8 h-8 rounded-lg text-xs font-black transition-colors ${currentPage === page ? 'bg-indigo-500 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>{page}</button>
                    );
                  })}
                  <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-2 py-1 rounded-lg text-xs font-bold text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed">{'>'}</button>
                  <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="px-2 py-1 rounded-lg text-xs font-bold text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed">{'>>'}</button>
                </div>
              </div>
            )}
        </div>
      )}

      {selectedLead && (
        <LeadDetailsModal
          key={selectedLead.id}
          lead={selectedLead}
          currentUser={user}
          companyUsers={users}
          currency={company.currency}
          revenueBrackets={company.revenueBrackets}
          onClose={() => setSelectedLead(null)}
          onStatusChange={async (status) => {
            const updated = await api.updateLead(selectedLead.id, { status });
            handleLeadUpdate(updated);
          }}
          onUpdate={handleLeadUpdate}
          onDelete={handleDeleteLead}
        />
      )}

      {showAddLead && (
        <AddLeadModal currentUser={user} onClose={() => setShowAddLead(false)} onSubmit={handleAddLead} />
      )}
    </div>
  );
};

export default UserDashboard;
